/* DE — Product gallery navigation
   Targets only <media-gallery><slider-component id="GalleryViewer-…">
   - Adds ArrowLeft / ArrowRight keyboard navigation
   - One image per trackpad/wheel gesture on desktop
   - One image per touch swipe on mobile/iPad
   - Wrap-around: last → next → first; first → prev → last
   - Reuses the existing scrollLeft + slideChanged plumbing so the
     counter and thumbnail aria-current stay in sync automatically.
*/
(function () {
  'use strict';

  // Debounce/cooldown for trackpad: a continuous swipe fires many wheel
  // events; we want one image per gesture, not per event.
  var WHEEL_COOLDOWN_MS = 450;
  // Minimum horizontal travel before a touch swipe commits.
  var TOUCH_THRESHOLD_PX = 30;
  // Minimum wheel deltaX before we treat it as intentional.
  var WHEEL_THRESHOLD_PX = 8;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function setupGallery(slider) {
    if (slider.__deNavWired) return;
    slider.__deNavWired = true;

    var ul = slider.querySelector('.product__media-list');
    if (!ul) return;

    function slides() {
      return Array.prototype.filter.call(
        ul.querySelectorAll('.slider__slide'),
        function (el) { return el.clientWidth > 0; }
      );
    }
    function slideOffset(list) {
      list = list || slides();
      return list.length >= 2 ? list[1].offsetLeft - list[0].offsetLeft : 0;
    }
    function currentIndex(list) {
      list = list || slides();
      var off = slideOffset(list);
      if (off <= 0) return 0;
      return Math.round(ul.scrollLeft / off);
    }

    function navigate(direction) {
      var list = slides();
      var n = list.length;
      if (n < 2) return;
      var cur = currentIndex(list);
      var next = ((cur + direction) % n + n) % n; // safe modulo
      var target = list[next];
      if (!target) return;

      // Update is-active class so thumbnail-style layouts stay correct.
      list.forEach(function (s) { s.classList.remove('is-active'); });
      target.classList.add('is-active');

      // The native scroll event will fire 'slideChanged' via the existing
      // SliderComponent.update(), which keeps the counter and thumbnails
      // in sync. Smooth scroll respects prefers-reduced-motion via the
      // existing CSS `scroll-behavior: smooth`.
      ul.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });

      keepArrowsEnabled();
    }

    // Because we now loop, the prev/next buttons must never look disabled.
    // SliderComponent.update() re-applies [disabled] on scroll edges, so we
    // strip it after every scroll tick (our listener runs after update()).
    var prevBtn = slider.querySelector('button[name="previous"]');
    var nextBtn = slider.querySelector('button[name="next"]');
    function keepArrowsEnabled() {
      if (prevBtn) prevBtn.removeAttribute('disabled');
      if (nextBtn) nextBtn.removeAttribute('disabled');
    }
    ul.addEventListener('scroll', keepArrowsEnabled, { passive: true });
    keepArrowsEnabled();

    /* ---------- 1. Prev / Next button click — add wrap-around ---------- */
    [prevBtn, nextBtn].forEach(function (btn) {
      if (!btn) return;
      btn.addEventListener('click', function (e) {
        // Capture phase + stopImmediatePropagation prevents the bubble-phase
        // handler in SliderComponent from also running.
        e.preventDefault();
        e.stopImmediatePropagation();
        navigate(btn.name === 'next' ? 1 : -1);
      }, true);
    });

    /* ---------- 2. Keyboard arrows ---------- */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

      // Don't grab arrows when the user is typing.
      var t = e.target;
      if (t && (
        t.tagName === 'INPUT' ||
        t.tagName === 'TEXTAREA' ||
        t.tagName === 'SELECT' ||
        t.isContentEditable
      )) return;

      // Only act if the gallery is at least partially visible — so a user
      // scrolled far below the gallery doesn't accidentally page through it.
      var rect = slider.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var inView = rect.bottom > 0 && rect.top < vh;
      if (!inView) return;

      e.preventDefault();
      navigate(e.key === 'ArrowRight' ? 1 : -1);
    });

    /* ---------- 3. Trackpad / mouse-wheel — desktop only ---------- */
    var wheelLocked = false;
    var wheelTimer = null;
    ul.addEventListener('wheel', function (e) {
      if (!window.matchMedia('(min-width: 750px)').matches) return;

      var dx = e.deltaX;
      var dy = e.deltaY;
      // Treat as horizontal intent when deltaX dominates, or shift+vertical.
      var horizontal = Math.abs(dx) > Math.abs(dy) || e.shiftKey;
      if (!horizontal) return;
      // Use deltaX, falling back to shifted deltaY.
      var delta = dx !== 0 ? dx : dy;
      if (Math.abs(delta) < WHEEL_THRESHOLD_PX) return;

      e.preventDefault();
      if (wheelLocked) return;
      wheelLocked = true;
      navigate(delta > 0 ? 1 : -1);

      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(function () { wheelLocked = false; }, WHEEL_COOLDOWN_MS);
    }, { passive: false });

    /* ---------- 4. Touch swipe — mobile / iPad ---------- */
    var tStartX = null, tStartY = null, tCommitted = false;

    ul.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) { tStartX = null; return; } // ignore pinch
      tStartX = e.touches[0].clientX;
      tStartY = e.touches[0].clientY;
      tCommitted = false;
    }, { passive: true });

    ul.addEventListener('touchmove', function (e) {
      if (tStartX === null || tCommitted || e.touches.length !== 1) return;
      var dx = e.touches[0].clientX - tStartX;
      var dy = e.touches[0].clientY - tStartY;
      // Below threshold: don't decide yet — let native handle vertical scroll.
      if (Math.abs(dx) < TOUCH_THRESHOLD_PX) return;
      // Vertical-dominant: leave it alone, page should scroll.
      if (Math.abs(dx) <= Math.abs(dy)) return;

      // Horizontal swipe committed — take over so native momentum + scroll-
      // snap can't carry us past the next image.
      e.preventDefault();
      tCommitted = true;
      navigate(dx < 0 ? 1 : -1); // swipe left = next image
    }, { passive: false });

    ul.addEventListener('touchend', function () {
      tStartX = null; tStartY = null;
    }, { passive: true });

    ul.addEventListener('touchcancel', function () {
      tStartX = null; tStartY = null;
    }, { passive: true });
  }

  function init() {
    var galleries = document.querySelectorAll(
      'media-gallery slider-component[id^="GalleryViewer"]'
    );
    galleries.forEach(setupGallery);
  }

  ready(init);

  // Re-run after Shopify section reloads (theme editor / variant changes).
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);
})();
